import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Calendar, Link2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function ReportsShortUrlsPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getShortUrlReport(period);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch short URL report');
    } finally {
      setLoading(false);
    }
  };

  const dailyClicks = useMemo(
    () => (reportData?.daily_clicks || []).map((item) => ({
      ...item,
      clicks: Number(item.clicks || 0),
    })),
    [reportData]
  );

  const topLinks = useMemo(
    () => (reportData?.top_links || []).map((item) => ({
      ...item,
      clicks: Number(item.clicks || 0),
      label: item.name || item.code || '-'
    })),
    [reportData]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-short-urls-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Short URLs</h1>
          <p className="text-[#A8A8A8] mt-1">Clicks and engagement for short links</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#A8A8A8]">Total Clicks</p>
            <p className="text-2xl font-bold text-[#2D2420]">{reportData?.total_clicks || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#A8A8A8]">Unique Links</p>
            <p className="text-2xl font-bold text-[#2D2420]">{reportData?.unique_links || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C97A5E]/10 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-[#C97A5E]" />
            </div>
            <div>
              <p className="text-xs text-[#A8A8A8]">Top Link</p>
              <p className="text-sm font-semibold text-[#2D2420]">
                {topLinks[0]?.label || '-'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Daily Clicks</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyClicks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="clicks" stroke="#C97A5E" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Top Links</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topLinks} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="clicks" fill="#C97A5E" radius={[6, 6, 6, 6]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
